import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { WbButton } from "../components/wb";
import { cameraCall } from "../lib/camera/client";
import { cameraStore, type Device } from "../lib/camera/store";
import { StationRecorder } from "../lib/camera/recorder";

export function CameraStationPage() {
  const [device, setDevice] = useState<Device | null>(null);
  const [message, setMessage] = useState(
    "Log op deze iPhone in en activeer de camera.",
  );
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [audio, setAudio] = useState(false);
  const video = useRef<HTMLVideoElement>(null);
  const recorder = useRef<StationRecorder | null>(null);
  const pending = useLiveQuery(() => cameraStore.clips.count(), [], 0);
  useEffect(() => {
    void cameraStore.devices
      .toArray()
      .then((rows) => setDevice(rows[0] || null))
      .catch(() =>
        setMessage("Lokale opslag niet beschikbaar. Camera niet activeren."),
      );
    return () => recorder.current?.close();
  }, []);
  async function enable() {
    if (!video.current || busy || enabled) return;
    setBusy(true);
    try {
      let d = device;
      if (!d) {
        const reply = await cameraCall("register", { name: "Wasserijcamera" });
        if (!reply.station || !reply.token)
          throw new Error("Registratie niet bevestigd");
        d = {
          id: reply.station.id,
          name: reply.station.name,
          token: reply.token,
        };
        await cameraStore.devices.put(d);
        setDevice(d);
      } else await cameraCall("renew", { stationId: d.id, token: d.token });
      const engine = new StationRecorder(d, video.current, setMessage);
      recorder.current = engine;
      await engine.enable(audio);
      setEnabled(true);
    } catch (e) {
      recorder.current?.close();
      recorder.current = null;
      setMessage(e instanceof Error ? e.message : "Camera niet gestart");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="space-y-4 p-3">
      <h1 className="text-2xl font-bold">Wasserijcamera</h1>
      <p>
        Deze telefoon filmt. Open op de tablet een kleed bij Reinigen of Social
        en kies de vaste camera.
      </p>
      <p className="font-semibold">
        Houd Safari zichtbaar, scherm ontgrendeld en de telefoon op stroom. De
        opname stopt bij tabwisselen. De sleutel blijft 30 dagen geldig en
        verlengt zichzelf zolang de camera in gebruik is.
      </p>
      {device && (
        <p>
          Camera: {device.name} · {device.id.slice(0, 8)}
        </p>
      )}
      <video
        ref={video}
        muted
        playsInline
        autoPlay
        className="w-full max-h-[55vh] bg-black rounded-lg object-contain"
      />
      <p
        role="status"
        className="font-semibold border-2 border-current rounded p-3"
      >
        {message}
      </p>
      <p>
        Lokaal wachtend: {pending}. Wis browsergegevens niet zolang hier opnames
        staan.
      </p>
      {!enabled && (
        <label className="flex items-center gap-3 min-h-12">
          <input
            type="checkbox"
            checked={audio}
            onChange={(e) => setAudio(e.target.checked)}
          />
          Wasserijgeluid opnemen (voor social mag het aan; bij gesprekken uit)
        </label>
      )}
      <div className="flex flex-wrap gap-3">
        {!enabled && (
          <WbButton disabled={busy} onClick={() => void enable()}>
            {busy ? "Camera starten…" : "Camera activeren"}
          </WbButton>
        )}
        {enabled && (
          <WbButton
            variant="secondary"
            onClick={() =>
              recorder.current?.stop(false, "Gestopt op de iPhone")
            }
          >
            Opname hier stoppen
          </WbButton>
        )}
        <Link to="/social" className="underline py-3">
          Terug naar Social (camera sluit)
        </Link>
      </div>
      <p className="text-sm">
        Opnamedoel 1080p / 25 fps op 8 Mbit/s, maximaal 2,5 minuut. Safari bepaalt de
        werkelijke instellingen; die verschijnen ook op de tablet. De
        voorvertoning is ongeveer iedere 2 seconden een foto.
      </p>
    </div>
  );
}
